import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/* version3's SetupGuide, class for class. */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const BORDER = 'var(--hd-border)';

/* pointer-events-none fixed bottom-20 right-4 z-[65] flex justify-end */
export const GuideDock = styled.div`
  ${brandTheme}
  position: fixed;
  right: 16px;
  bottom: 80px;
  z-index: 65;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

/* w-[min(92vw,19rem)] scale-[0.92] origin-bottom-right rounded-2xl border bg-card shadow-elevated */
export const GuideCard = styled.div`
  width: min(92vw, 19rem);
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: var(--hd-card);
  box-shadow: var(--hd-shadow-elevated);
  color: ${FG};
  transform: scale(0.92);
  transform-origin: bottom right;
  pointer-events: auto;
  transition: all 0.15s ease;
`;

export const GuideHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 0;

  && h2 {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
  }

  .count {
    font-size: 12px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  button {
    display: grid;
    place-items: center;
    padding: 0;
    border: none;
    background: none;
    color: ${MUTED_FG};
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: ${FG};
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const GuideProgress = styled.div`
  padding: 12px 20px 0;

  .track {
    width: 100%;
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--hd-muted);
  }

  .fill {
    height: 100%;
    border-radius: 999px;
    background: var(--hd-gradient-houdiny);
    transition: all 0.5s ease;
  }
`;

export const GuideNext = styled.p`
  && {
    margin: 0;
    padding: 12px 20px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  button {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-weight: 500;
    color: var(--hd-primary);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .finished {
    font-weight: 500;
    color: var(--hd-primary);
  }
`;

export const GuideList = styled.div`
  margin-top: 12px;
  padding-bottom: 12px;
`;

export const GuideTask = styled.div`
  border-top: 1px solid color-mix(in srgb, var(--hd-border) 70%, transparent);
  background: ${({ $open }) => ($open ? 'color-mix(in srgb, var(--hd-muted) 40%, transparent)' : 'transparent')};

  &:first-child {
    border-top: 0;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 20px;
    border: none;
    text-align: left;
    background: none;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }

  .check {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 20px;
    height: 20px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    background: var(--hd-background);
    color: #fff;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &.done {
      border-color: transparent;
      background: var(--hd-gradient-houdiny);
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};

    &.done {
      color: ${MUTED_FG};
      text-decoration: line-through;
    }

    &.locked {
      color: ${MUTED_FG};
    }
  }

  .indicator {
    display: grid;
    place-items: center;
    margin-left: auto;
    color: ${MUTED_FG};

    .chevron {
      width: 16px;
      height: 16px;
      transition: transform 0.15s ease;

      &.open {
        transform: rotate(180deg);
      }
    }

    .lock {
      width: 14px;
      height: 14px;
    }
  }

  .panel {
    padding: 0 20px 16px 52px;
    animation: ${fadeIn} 0.15s ease-out;

    p {
      margin: 0;
      font-size: 12px;
      line-height: 1.625;
      color: ${MUTED_FG};
    }
  }

  .panel-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    height: 36px;
    border-radius: 14px;
    font-size: 12px;
    cursor: pointer;
  }

  .cta {
    padding: 0 14px;
    border: none;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
  }

`;
